import {
  ClipboardList,
  Database,
  Gauge,
  Images,
  type LucideIcon,
  Package,
  Search,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  type SubsidiaryScopedNavItem,
  SubsidiaryScopedTabsLayout,
} from '~/features/platform/components/subsidiary-scoped-tabs-layout';

/**
 * Item in the Explorer tab bar. Add a new entry here (plus its route file under
 * `routes/.../explorer/`) to surface another browsing surface. Connector and
 * data-flow administration lives in the Integrations section instead — see
 * `integrations-settings-layout.tsx`.
 */
interface ExplorerNavItem {
  to: string;
  /** i18n key under `explorer:layout.nav`. */
  labelKey: string;
  /** Leading icon shown in the tab. */
  icon: LucideIcon;
  /** Only show to super admins (the route is guarded separately). */
  superAdminOnly?: boolean;
  /** The page reads every subsidiary until one is picked: the picker offers "All subsidiaries". */
  offersAllSubsidiaries?: boolean;
}

const navItems: ExplorerNavItem[] = [
  { to: '/explorer/products', labelKey: 'products', icon: Package, superAdminOnly: true },
  {
    to: '/explorer/keyword-planner',
    labelKey: 'keywordPlanner',
    icon: Search,
    superAdminOnly: true,
  },
  { to: '/explorer/data-quality', labelKey: 'dataQuality', icon: Gauge, superAdminOnly: true },
  {
    to: '/explorer/coverage',
    labelKey: 'coverageReport',
    icon: ClipboardList,
    superAdminOnly: true,
    offersAllSubsidiaries: true,
  },
  { to: '/explorer/datalake', labelKey: 'datalake', icon: Database, superAdminOnly: true },
  { to: '/explorer/clickhouse', labelKey: 'clickhouse', icon: Database, superAdminOnly: true },
  {
    to: '/explorer/catalog-images',
    labelKey: 'catalogImages',
    icon: Images,
    superAdminOnly: true,
  },
];

/**
 * Layout shell for the Explorer section — supplies its header copy and tab config
 * to the shared subsidiary-scoped shell. Mounted by `/explorer/route.tsx`; child
 * pages render into the shell's `<Outlet/>`.
 */
export function ExplorerLayout() {
  const { t } = useTranslation('explorer');

  const items: SubsidiaryScopedNavItem[] = navItems.map((item) => ({
    to: item.to,
    label: t(`layout.nav.${item.labelKey}`),
    icon: item.icon,
    ...(item.superAdminOnly ? { superAdminOnly: true } : {}),
    ...(item.offersAllSubsidiaries ? { allSubsidiariesLabel: t('layout.allSubsidiaries') } : {}),
  }));

  return (
    <SubsidiaryScopedTabsLayout
      title={t('layout.title')}
      subtitle={t('layout.subtitle')}
      navAriaLabel={t('layout.navAriaLabel')}
      navItems={items}
    />
  );
}
